import { useEffect, useRef } from "react";

/* ── BOSS SHIP IMAGE PATH ──────────────────────────────────────
   Set this to the path of your boss ship image.
   The image will be drawn in place of the canvas-rendered sprite.
   Leave as empty string '' to use the canvas-drawn fallback.
   The image is expected to be a top-down view facing downward.
   Recommended size: ~380×200px with transparent/black background.
   ─────────────────────────────────────────────────────────────── */
const BOSS_SHIP_IMG_PATH = '/ChatGPT Image Mar 4, 2026, 03_48_19 AM.png';

/*
  WARP DRIVE v5 — LIVE STARFIELD EDITION
  ══════════════════════════════════════════════════════════
  BACKGROUND — "Flying through a real galaxy":
    · Baked nebula layer (7 rich clouds + galaxy arm) — 1 drawImage
    · 300 live moving stars scrolling downward at uniform speed
      (no parallax — every star moves identically so it feels
       like you're drifting through open space, not a tunnel)
    · Stars are always ROUND (arc, never fillRect)
    · Each star has its own twinkle phase + speed → dim↔bright
    · 5-tier colour palette:
        cold blue-white  45%  — dominant star field colour
        warm white       22%  — slightly yellow stars
        gold             14%  — warm giant stars
        rose/violet      11%  — exotic nebula stars
        aqua/teal         8%  — adds sci-fi variety
    · Size tiers: micro (0.6-1.0), small (1.0-1.8),
                  medium (1.8-2.8), bright (2.8-4.2)
    · Bright stars get a soft radial bloom halo
    · Stars that scroll off-bottom wrap back to the top
      at a random X so the field never depletes
    · Boss mode: scroll speed ×2.2, twinkle rate up,
                 nebula cross-fades to red-shift version
  ══════════════════════════════════════════════════════════
*/

const FONTS = "https://fonts.googleapis.com/css2?family=Orbitron:wght@400;700;900&family=JetBrains+Mono:wght@400;700&family=Rajdhani:wght@600;700&display=swap";

const CSS = `
@import url('${FONTS}');
*{margin:0;padding:0;box-sizing:border-box}
html,body,#root{width:100%;height:100%;overflow:hidden;background:#000}
.wd{width:100vw;height:100vh;position:relative;overflow:hidden;background:#000}

/* TERMINAL */
.tw{position:fixed;inset:0;background:#0c0c0c;display:flex;align-items:center;justify-content:center;z-index:100}
.tbox{width:min(860px,95vw);height:min(560px,88vh);background:#0f0f0f;border:1px solid #1e1e1e;border-radius:6px;display:flex;flex-direction:column;overflow:hidden}
.tbar{background:#161616;padding:10px 16px;display:flex;align-items:center;gap:8px;border-bottom:1px solid #1c1c1c;flex-shrink:0}
.tdot{width:11px;height:11px;border-radius:50%}
.tdr{background:#ff5f57}.tdy{background:#febc2e}.tdg{background:#28c840}
.ttl{color:#2a2a2a;font-size:10px;margin:0 auto;letter-spacing:2px;text-transform:uppercase;font-family:'JetBrains Mono',monospace}
.tscroll{flex:1;overflow-y:auto;padding:16px 20px 4px;background:#0f0f0f;scrollbar-width:thin;scrollbar-color:#1e1e1e #0f0f0f}
.tscroll::-webkit-scrollbar{width:3px}
.tscroll::-webkit-scrollbar-thumb{background:#1e1e1e}
.tout{font-size:12px;line-height:1.85;color:#444;font-family:'JetBrains Mono',monospace}
.tout .hi{color:#b8b8b8}.tout .p2{color:#666}
.tout .cmd{color:#d8d8d8}.tout .ok{color:#888}
.tout .warn{color:#665522}.tout .dim{color:#2e2e2e}.tout .err{color:#774444}
.tcur{display:inline-block;width:7px;height:12px;background:#777;animation:tc .9s step-end infinite;margin-left:1px;vertical-align:text-bottom;border-radius:1px}
@keyframes tc{0%,49%{opacity:.8}50%,100%{opacity:0}}
.tinrow{display:flex;align-items:center;padding:7px 20px 12px;border-top:1px solid #181818;gap:6px;flex-shrink:0;background:#0f0f0f}
.tprompt{color:#2a2a2a;font-size:12px;white-space:nowrap;font-family:'JetBrains Mono',monospace}
.tprompt .hi{color:#b8b8b8}.tprompt .p2{color:#666}
.tinp{flex:1;background:transparent;border:none;outline:none;color:#d8d8d8;font-family:'JetBrains Mono',monospace;font-size:12px;caret-color:#888}

/* WARP INTRO */
.wi{position:fixed;inset:0;background:#000;z-index:200;display:none;align-items:center;justify-content:center;flex-direction:column}
.wi.on{display:flex}
.wit{font-family:'Orbitron',sans-serif;font-size:clamp(3rem,9vw,6.5rem);font-weight:900;letter-spacing:.12em;color:#fff;text-align:center;opacity:0;animation:wiT .45s ease forwards}
@keyframes wiT{to{opacity:1}}
.wil{width:0;height:1px;background:linear-gradient(90deg,transparent,rgba(88,166,255,.5),transparent);margin:16px auto;animation:wiL .55s .25s ease forwards}
@keyframes wiL{to{width:150px}}
.wis{font-family:'JetBrains Mono',monospace;font-size:.68rem;letter-spacing:.35em;text-transform:uppercase;color:rgba(88,166,255,.28);text-align:center;opacity:0;animation:wiS .35s .45s ease forwards;min-height:1.2em}
@keyframes wiS{to{opacity:1}}
.wic{position:absolute;width:26px;height:26px;border-color:rgba(88,166,255,.12);border-style:solid}
.wic.tl{top:18px;left:18px;border-width:1px 0 0 1px}
.wic.tr{top:18px;right:18px;border-width:1px 1px 0 0}
.wic.bl{bottom:18px;left:18px;border-width:0 0 1px 1px}
.wic.br{bottom:18px;right:18px;border-width:0 1px 1px 0}

/* GAME CANVAS */
.gc{position:fixed;inset:0;display:none;width:100vw;height:100vh}

/* HUD — all position:fixed */
.hud{position:fixed;inset:0;pointer-events:none;z-index:20;display:none}
.htop{position:absolute;top:0;left:0;right:0;height:54px;background:linear-gradient(180deg,rgba(0,0,0,.96) 0%,rgba(0,0,0,.52) 72%,transparent 100%);display:flex;align-items:center;padding:0 22px}
.htop::after{content:'';position:absolute;bottom:0;left:0;right:0;height:1px;background:linear-gradient(90deg,transparent,rgba(88,166,255,.08) 28%,rgba(88,166,255,.22) 50%,rgba(88,166,255,.08) 72%,transparent)}
.hsc{display:flex;flex-direction:column;gap:0;min-width:190px}
.hslbl{font-family:'Rajdhani',sans-serif;font-size:7px;font-weight:700;color:rgba(88,166,255,.24);letter-spacing:4px;text-transform:uppercase;line-height:1}
.hsval{font-family:'Orbitron',sans-serif;font-size:22px;font-weight:900;color:#58a6ff;letter-spacing:2px;line-height:1.1;text-shadow:0 0 12px rgba(88,166,255,.45)}
.hmid{flex:1;display:flex;flex-direction:column;align-items:center;gap:4px;padding:0 24px}
.hmrow{display:flex;justify-content:space-between;width:100%;max-width:320px}
.hmlbl{font-family:'Rajdhani',sans-serif;font-size:7px;font-weight:700;color:rgba(88,166,255,.18);letter-spacing:3px;text-transform:uppercase}
.hmval{font-family:'JetBrains Mono',monospace;font-size:8px;color:rgba(88,166,255,.28);letter-spacing:1px}
.htrack{width:100%;max-width:320px;height:3px;background:rgba(255,255,255,.04);border-radius:2px;position:relative}
.hfill{position:absolute;top:0;left:0;height:100%;border-radius:2px;transition:width .15s linear}
.hfill::after{content:'';position:absolute;right:-1px;top:-2px;bottom:-2px;width:3px;background:#fff;border-radius:2px;filter:blur(1px);opacity:.55}
.hrt{min-width:200px;display:flex;flex-direction:column;align-items:flex-end;gap:2px}
.hhint{font-family:'Rajdhani',sans-serif;font-size:8px;font-weight:700;color:rgba(255,255,255,.09);letter-spacing:1.5px}
.hboss{font-family:'Orbitron',sans-serif;font-size:9px;font-weight:900;color:#f85149;letter-spacing:2px;text-shadow:0 0 10px rgba(248,81,73,.5);display:none;animation:ba 1.1s ease-in-out infinite}
@keyframes ba{0%,100%{opacity:.38}50%{opacity:1}}
.hcombo{position:absolute;top:62px;left:22px;font-family:'Orbitron',sans-serif;font-size:10px;font-weight:900;color:#e3b341;letter-spacing:2px;padding:3px 10px;background:rgba(12,8,0,.96);border-left:2px solid #e3b341;border-radius:0 3px 3px 0;text-shadow:0 0 8px rgba(227,179,65,.4);display:none}

/* BOSS WARNING */
.bwarn{position:fixed;inset:0;z-index:300;pointer-events:none;display:none}
.bwarn.on{display:block}
.bwbg{position:absolute;inset:0;background:rgba(40,0,0,.08);animation:bwf .55s step-end infinite}
@keyframes bwf{0%,100%{opacity:1}50%{opacity:0}}
.bwscan{position:absolute;inset:0;background:repeating-linear-gradient(0deg,transparent,transparent 3px,rgba(248,81,73,.005) 3px,rgba(248,81,73,.005) 4px)}
.bwc{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);text-align:center}
.bweye{font-family:'JetBrains Mono',monospace;font-size:9px;letter-spacing:8px;color:rgba(248,81,73,.38);margin-bottom:12px;animation:bwp .8s ease-in-out infinite}
.bwmain{font-family:'Orbitron',sans-serif;font-size:clamp(38px,7vw,68px);font-weight:900;letter-spacing:10px;color:#f85149;text-shadow:0 0 24px rgba(248,81,73,.75),0 0 60px rgba(248,81,73,.28);animation:bwp .8s ease-in-out infinite}
.bwsub{font-family:'Rajdhani',sans-serif;font-size:12px;font-weight:700;letter-spacing:5px;color:rgba(248,81,73,.35);margin-top:12px;animation:bwp 1.1s ease-in-out infinite reverse}
.bwbars{display:flex;justify-content:center;gap:4px;margin-top:18px}
.bwbar{width:2px;background:#f85149;border-radius:1px;animation:bwb .4s ease-in-out infinite alternate}
.bwbar:nth-child(odd){height:26px}.bwbar:nth-child(even){height:14px;animation-delay:.2s}
@keyframes bwp{0%,100%{opacity:.38}50%{opacity:1}}
@keyframes bwb{from{opacity:.1;transform:scaleY(.15)}to{opacity:1;transform:scaleY(1)}}

/* LASER NOTIF */
.lnotif{position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);font-family:'Orbitron',sans-serif;font-size:18px;font-weight:900;color:#f85149;letter-spacing:4px;z-index:50;padding:12px 32px;border:1px solid rgba(248,81,73,.25);border-radius:6px;background:rgba(8,0,0,.96);display:none;animation:lIn .2s cubic-bezier(.34,1.56,.64,1) both}
@keyframes lIn{from{transform:translate(-50%,-50%) scale(.2);opacity:0}to{transform:translate(-50%,-50%) scale(1);opacity:1}}

/* GAME OVER */
.go{position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);background:#0b0f16;border:1px solid #1e2530;padding:44px 72px;border-radius:10px;text-align:center;z-index:400;display:none;animation:goIn .32s cubic-bezier(.34,1.56,.64,1) both}
.go.fail{border-color:rgba(248,81,73,.14)}
@keyframes goIn{from{transform:translate(-50%,-50%) scale(.2);opacity:0}to{transform:translate(-50%,-50%) scale(1);opacity:1}}
.goeye{font-family:'Rajdhani',sans-serif;font-size:8px;font-weight:700;letter-spacing:5px;color:rgba(88,166,255,.2);text-transform:uppercase;margin-bottom:8px}
.go.fail .goeye{color:rgba(248,81,73,.2)}
.goh{font-family:'Orbitron',sans-serif;font-size:24px;font-weight:900;letter-spacing:5px;background:linear-gradient(135deg,#79c0ff,#58a6ff);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text}
.go.fail .goh{background:linear-gradient(135deg,#ff7b72,#f85149);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text}
.goline{width:36px;height:1px;background:rgba(88,166,255,.14);margin:12px auto}
.go.fail .goline{background:rgba(248,81,73,.14)}
.gosc{font-family:'JetBrains Mono',monospace;font-size:11px;color:rgba(88,166,255,.38);letter-spacing:2px;margin-bottom:24px}
.go.fail .gosc{color:rgba(248,81,73,.38)}
.gobtn{background:transparent;color:rgba(88,166,255,.58);border:1px solid rgba(88,166,255,.14);padding:10px 36px;font-size:8px;font-weight:700;border-radius:5px;cursor:pointer;transition:all .14s;font-family:'Orbitron',sans-serif;letter-spacing:3px;pointer-events:auto}
.gobtn:hover{background:rgba(88,166,255,.04);border-color:rgba(88,166,255,.35);color:#79c0ff}
.go.fail .gobtn{color:rgba(248,81,73,.58);border-color:rgba(248,81,73,.14)}
.go.fail .gobtn:hover{background:rgba(248,81,73,.04);border-color:rgba(248,81,73,.35);color:#ff7b72}

/* CORNER BRACKETS */
.cb{position:fixed;width:16px;height:16px;border-color:rgba(88,166,255,.055);border-style:solid;pointer-events:none;z-index:8}
.cbtl{top:5px;left:5px;border-width:1px 0 0 1px}
.cbtr{top:5px;right:5px;border-width:1px 1px 0 0}
.cbbl{bottom:5px;left:5px;border-width:0 0 1px 1px}
.cbbr{bottom:5px;right:5px;border-width:0 1px 1px 0}

/* MOBILE */
.mob{position:fixed;bottom:22px;left:0;width:100%;display:none;justify-content:space-between;padding:0 22px;z-index:50;pointer-events:none}
@media(hover:none)and(pointer:coarse){.mob{display:flex}}
.mobbtn{width:60px;height:60px;border-radius:50%;background:rgba(88,166,255,.03);border:1px solid rgba(88,166,255,.12);color:rgba(88,166,255,.4);font-size:20px;display:flex;align-items:center;justify-content:center;pointer-events:auto;user-select:none;-webkit-user-select:none;touch-action:manipulation;-webkit-tap-highlight-color:transparent;font-family:'Orbitron',sans-serif}
.mobbtn:active{background:rgba(88,166,255,.09)}
`;

/* ── BOOT / LAUNCH SEQUENCES ── */
const BOOT=[
  {t:'type',html:'<span class="hi">pilot@warp-drive</span><span class="dim">:</span><span class="p2">~</span><span class="dim">$</span> <span class="cmd">sudo apt install warp-drive</span>',d:340},
  {t:'line',html:'[sudo] password for pilot: ',d:500},{t:'line',html:'',d:90},
  {t:'line',html:'Reading package lists... Done',d:46},
  {t:'line',html:'Building dependency tree... Done',d:46},
  {t:'line',html:'The following NEW packages will be installed:',d:32},
  {t:'line',html:'  <span class="ok">warp-drive</span>  game-engine  libplasmaphysics2  libbossai1',d:32},
  {t:'line',html:'0 upgraded, 4 newly installed.',d:32},
  {t:'line',html:'Need to get 142 MB of archives.',d:32},
  {t:'line',html:'<span class="warn">Do you want to continue? [Y/n]</span> y',d:370},
  {t:'line',html:'',d:52},
  {t:'line',html:'Get:1 libplasmaphysics2 3.1.2 [28.4 MB]',d:32},
  {t:'line',html:'Get:2 libbossai1 1.8.0 [11.2 MB]',d:32},
  {t:'line',html:'Get:3 game-engine 5.4.1 [62.0 MB]',d:32},
  {t:'line',html:'Get:4 warp-drive 2.1.0 [40.8 MB]',d:32},
  {t:'line',html:'Fetched 142 MB in 3s (47.3 MB/s)',d:155},
  {t:'line',html:'Unpacking libplasmaphysics2 (3.1.2) ...',d:24},
  {t:'line',html:'Unpacking libbossai1 (1.8.0) ...',d:24},
  {t:'line',html:'Unpacking game-engine (5.4.1) ...',d:24},
  {t:'line',html:'Unpacking warp-drive (2.1.0) ...',d:24},
  {t:'line',html:'Setting up warp-drive (2.1.0) ...',d:34},
  {t:'line',html:'',d:42},
  {t:'line',html:'<span class="ok">✔ warp-drive installed successfully.</span>',d:105},
  {t:'line',html:'<span class="dim">Type <span class="ok">sudo warp-drive</span> to play · <span class="ok">help</span> for commands · <span class="ok">exit</span> to leave</span>',d:0,final:true},
];
const LAUNCH=[
  {html:'Initializing plasma engine...   <span class="ok">OK</span>',d:48},
  {html:'Loading asset manifests...      <span class="ok">OK</span>',d:48},
  {html:'Spawning player vessel...       <span class="ok">OK</span>',d:48},
  {html:'Arming auto-fire systems...     <span class="ok">OK</span>',d:48},
  {html:'Compiling boss AI (Aries)...    <span class="ok">OK</span>',d:52},
  {html:'Locking laser (req: 500pts)...  <span class="warn">LOCKED</span>',d:58},
  {html:'',d:34},
  {html:'<span class="ok">All systems nominal.</span> Engaging warp drive...',d:230},
];

/* ── SPRITE BUILDERS ── */
const mkC=(w,h)=>{const c=document.createElement('canvas');c.width=w;c.height=h;return c;};

function buildPlayer(boss){
  const W=74,H=74,pad=12,pw=50,ph=50,cv=mkC(W,H),c=cv.getContext('2d');
  const px=pad,py=pad,pcx=px+pw/2;
  const sg=c.createLinearGradient(px,0,px+pw,0);
  if(boss){sg.addColorStop(0,'#0e7490');sg.addColorStop(.35,'#06b6d4');sg.addColorStop(.5,'#22d3ee');sg.addColorStop(.65,'#06b6d4');sg.addColorStop(1,'#0e7490');}
  else{sg.addColorStop(0,'#1d4ed8');sg.addColorStop(.35,'#3b82f6');sg.addColorStop(.5,'#60a5fa');sg.addColorStop(.65,'#3b82f6');sg.addColorStop(1,'#1d4ed8');}
  c.fillStyle=sg;
  c.beginPath();c.moveTo(pcx,py);c.lineTo(px+pw*.35,py+ph*.25);c.lineTo(px+pw*.35,py+ph*.55);c.lineTo(px+pw*.4,py+ph*.7);c.lineTo(pcx,py+ph*.65);c.lineTo(px+pw*.6,py+ph*.7);c.lineTo(px+pw*.65,py+ph*.55);c.lineTo(px+pw*.65,py+ph*.25);c.closePath();c.fill();
  const wo=boss?9:6;
  c.fillStyle=boss?'#0e7490':'#1e40af';
  c.beginPath();c.moveTo(px+pw*.35,py+ph*.42);c.lineTo(px-wo,py+ph*.52);c.lineTo(px-(wo-3),py+ph*.76);c.lineTo(px+pw*.25,py+ph*.74);c.lineTo(px+pw*.35,py+ph*.6);c.closePath();c.fill();
  c.beginPath();c.moveTo(px+pw*.65,py+ph*.42);c.lineTo(px+pw+wo,py+ph*.52);c.lineTo(px+pw+(wo-3),py+ph*.76);c.lineTo(px+pw*.75,py+ph*.74);c.lineTo(px+pw*.65,py+ph*.6);c.closePath();c.fill();
  const cr=boss?10:8,cg=c.createRadialGradient(pcx,py+ph*.3,0,pcx,py+ph*.3,cr);
  cg.addColorStop(0,boss?'#a5f3fc':'#bfdbfe');cg.addColorStop(.5,'#3b82f6');cg.addColorStop(1,'#1e3a8a');
  c.fillStyle=cg;c.beginPath();c.ellipse(pcx,py+ph*.3,cr-2,cr,0,0,6.2832);c.fill();
  return cv;
}
function buildBoss(enraged,flash){
  /* Canvas: 380x200. BW=300/BH=180 = gameplay hitbox. PAD=40 each side for wing overhangs.
     Ship orientation: top of canvas = REAR (engines), bottom = FRONT (weapons toward player).
     Heavy crimson industrial battlecruiser, top-down view. */
  const BW=300,BH=180,PAD=40,CW=BW+PAD*2,CH=BH+20,cv=mkC(CW,CH),c=cv.getContext('2d');
  const bcx=PAD+BW/2; // canvas center-x = 190
  const fl=flash>0.25,enr=enraged;
  // Colour palette
  const VOID='#060101';
  const DARK=fl?'#3a0808':enr?'#3d0606':'#280404';
  const BASE=fl?'#6a1010':enr?'#6b0e0e':'#4a0a0a';
  const MID =fl?'#9a1818':enr?'#961212':'#720f0f';
  const BRIG=fl?'#cc2020':enr?'#c41616':'#941313';
  const HI  =fl?'#e83030':enr?'#e01a1a':'#b61414';
  const PANEL='#0e0101';
  const GLOW=enr?'rgba(255,30,0,0.9)':'rgba(210,20,0,0.85)';
  const GDIM=enr?'rgba(255,60,0,0.5)':'rgba(180,20,0,0.5)';

  function rct(x,y,w,h,col){c.fillStyle=col;c.fillRect(x,y,w,h);}
  function pnl(x,y,w,h,col){
    c.fillStyle=col;c.fillRect(x,y,w,h);
    c.strokeStyle=PANEL;c.lineWidth=0.8;c.strokeRect(x+0.5,y+0.5,w-1,h-1);
    c.fillStyle='rgba(255,80,80,0.06)';c.fillRect(x,y,w,1);c.fillRect(x,y,1,h);
  }
  function grd(x,y,w,h,cols,rows){
    c.strokeStyle=PANEL;c.lineWidth=0.6;c.beginPath();
    const cw=w/cols,rh=h/rows;
    for(let i=1;i<cols;i++){c.moveTo(x+i*cw,y);c.lineTo(x+i*cw,y+h);}
    for(let j=1;j<rows;j++){c.moveTo(x,y+j*rh);c.lineTo(x+w,y+j*rh);}
    c.stroke();
  }
  function engGlow(ex,ey,r){
    const g=c.createRadialGradient(ex,ey,0,ex,ey,r);
    g.addColorStop(0,'rgba(255,230,160,1)');g.addColorStop(0.15,'rgba(255,120,10,0.95)');
    g.addColorStop(0.5,'rgba(200,40,0,0.4)');g.addColorStop(1,'rgba(0,0,0,0)');
    c.fillStyle=g;c.beginPath();c.arc(ex,ey,r,0,6.2832);c.fill();
  }
  function gDot(x,y,r,col,dim){
    c.fillStyle=col;c.beginPath();c.arc(x,y,r,0,6.2832);c.fill();
    const g=c.createRadialGradient(x,y,0,x,y,r*4);
    g.addColorStop(0,dim);g.addColorStop(1,'rgba(0,0,0,0)');
    c.fillStyle=g;c.beginPath();c.arc(x,y,r*4,0,6.2832);c.fill();
  }
  function brl(x,y,w,h){
    rct(x-w/2,y,w,h,VOID);rct(x-w/2+1,y+1,w-2,h-3,DARK);
    rct(x-0.5,y,1,h,MID);
  }

  // 0. ENGINE EXHAUST GLOW
  [bcx-90,bcx-30,bcx+30,bcx+90].forEach(ex=>engGlow(ex,2,20));
  [bcx-55,bcx+55].forEach(ex=>engGlow(ex,5,11));

  // 1. AFT TRANSOM y=5..45
  c.fillStyle=BASE;c.beginPath();
  c.moveTo(bcx-130,5);c.lineTo(bcx+130,5);c.lineTo(bcx+140,18);c.lineTo(bcx+140,45);
  c.lineTo(bcx-140,45);c.lineTo(bcx-140,18);c.closePath();c.fill();
  c.strokeStyle=PANEL;c.lineWidth=0.8;c.stroke();
  grd(bcx-140,5,280,40,7,2);
  [bcx-90,bcx-30,bcx+30,bcx+90].forEach(ex=>{
    pnl(ex-14,6,28,38,DARK);pnl(ex-10,9,20,32,BASE);grd(ex-10,9,20,32,2,3);
    c.strokeStyle=MID;c.lineWidth=1.5;c.beginPath();c.arc(ex,8,8,0,6.2832);c.stroke();
    c.fillStyle=VOID;c.beginPath();c.arc(ex,8,6,0,6.2832);c.fill();
    c.fillStyle='rgba(255,120,10,0.95)';c.beginPath();c.arc(ex,8,3.5,0,6.2832);c.fill();
  });
  pnl(bcx-50,8,100,34,MID);grd(bcx-50,8,100,34,5,2);
  pnl(bcx-24,11,48,28,BRIG);grd(bcx-24,11,48,28,3,2);
  for(let i=0;i<4;i++){rct(bcx-16+i*10,16,7,5,VOID);gDot(bcx-12+i*10,18,1.5,GLOW,GDIM);}

  // 2. WING SPONSONS
  [-1,1].forEach(sd=>{
    const hx=bcx+sd*150,wx=bcx+sd*175;
    c.fillStyle=BASE;c.beginPath();
    if(sd<0){c.moveTo(hx,42);c.lineTo(wx+6,55);c.lineTo(wx,72);c.lineTo(wx,118);c.lineTo(wx+6,130);c.lineTo(hx,138);}
    else{c.moveTo(hx,42);c.lineTo(wx-6,55);c.lineTo(wx,72);c.lineTo(wx,118);c.lineTo(wx-6,130);c.lineTo(hx,138);}
    c.closePath();c.fill();c.strokeStyle=PANEL;c.lineWidth=0.8;c.stroke();
    const spx=sd<0?wx-2:hx,spw=Math.abs(wx-hx)+2;
    pnl(spx,62,spw,66,MID);grd(spx,62,spw,66,3,4);
    pnl(spx+(sd<0?4:0),72,spw-4,46,BASE);
    const gpx=sd<0?wx+4:wx-22;
    pnl(gpx,80,18,30,DARK);grd(gpx,80,18,30,2,3);
    brl(gpx+5,108,4,18);brl(gpx+13,108,4,18);
    c.fillStyle=DARK;c.beginPath();c.arc(gpx+9,92,7,0,6.2832);c.fill();
    c.strokeStyle=BASE;c.lineWidth=1;c.stroke();
    c.fillStyle=MID;c.beginPath();c.arc(gpx+9,92,4.5,0,6.2832);c.fill();
    gDot(gpx+9,92,2,GLOW,GDIM);
    gDot(bcx+sd*162,68,2.2,GLOW,GDIM);gDot(bcx+sd*162,100,2.2,GLOW,GDIM);gDot(bcx+sd*162,122,2.2,GLOW,GDIM);
    [55,126].forEach(sy=>{pnl(sd<0?wx-2:hx,sy,18,10,DARK);brl(sd<0?wx+7:hx+9,sy+8,3,14);});
  });

  // 3. MAIN HULL BODY y=45..148
  c.fillStyle=BASE;c.beginPath();
  c.moveTo(bcx-150,45);c.lineTo(bcx+150,45);c.lineTo(bcx+148,80);c.lineTo(bcx+142,148);
  c.lineTo(bcx-142,148);c.lineTo(bcx-148,80);c.closePath();c.fill();
  c.strokeStyle=PANEL;c.lineWidth=0.8;c.stroke();
  [55,68,82,98,114,130,144].forEach(yl=>{c.strokeStyle=PANEL;c.lineWidth=0.7;c.beginPath();c.moveTo(bcx-148,yl);c.lineTo(bcx+148,yl);c.stroke();});
  [-110,-70,-35,0,35,70,110].forEach(xo=>{c.strokeStyle=PANEL;c.lineWidth=0.6;c.beginPath();c.moveTo(bcx+xo,45);c.lineTo(bcx+xo,148);c.stroke();});
  [-1,1].forEach(sd=>{
    pnl(bcx+sd*100,52,46,90,MID);grd(bcx+sd*100,52,46,90,2,5);
    [65,88,110,128].forEach(yp=>rct(bcx+sd*(sd<0?104:106),yp,10,6,DARK));
    pnl(bcx+sd*100,55,44,28,BASE);pnl(bcx+sd*110,58,24,22,DARK);grd(bcx+sd*110,58,24,22,2,2);
    [72,90,108,126].forEach(yp=>gDot(bcx+sd*144,yp,1.8,GLOW,GDIM));
  });
  pnl(bcx-16,47,32,98,MID);grd(bcx-16,47,32,98,2,6);
  pnl(bcx-10,50,20,92,BRIG);grd(bcx-10,50,20,92,2,7);
  pnl(bcx-52,74,104,44,BRIG);grd(bcx-52,74,104,44,5,3);
  pnl(bcx-36,78,72,36,HI);grd(bcx-36,78,72,36,4,3);
  for(let i=0;i<5;i++){const wx=bcx-32+i*16;rct(wx,83,10,5,VOID);gDot(wx+5,85,1.5,GLOW,GDIM);}
  c.fillStyle=DARK;c.beginPath();c.arc(bcx,90,8,0,6.2832);c.fill();
  c.strokeStyle=MID;c.lineWidth=1.2;c.stroke();
  c.fillStyle=BRIG;c.beginPath();c.arc(bcx,90,5,0,6.2832);c.fill();
  gDot(bcx,90,2.5,GLOW,GDIM);
  [-1,1].forEach(sd=>{
    const bkx=bcx+sd*52;
    pnl(bkx-14,57,28,20,DARK);grd(bkx-14,57,28,20,2,2);
    brl(bkx-5,75,5,22);brl(bkx+5,75,5,22);
    c.fillStyle=DARK;c.beginPath();c.arc(bkx,62,8,0,6.2832);c.fill();
    c.strokeStyle=BASE;c.lineWidth=1;c.stroke();
    c.fillStyle=MID;c.beginPath();c.arc(bkx,62,5,0,6.2832);c.fill();
    gDot(bkx,62,2,GLOW,GDIM);
    pnl(bkx-12,120,24,22,DARK);brl(bkx-4,140,4,16);brl(bkx+4,140,4,16);
    c.fillStyle=DARK;c.beginPath();c.arc(bkx,126,6,0,6.2832);c.fill();
    c.strokeStyle=BASE;c.lineWidth=1;c.stroke();
    c.fillStyle=BASE;c.beginPath();c.arc(bkx,126,3.5,0,6.2832);c.fill();
    gDot(bkx,126,1.8,GLOW,GDIM);
  });
  c.strokeStyle=DARK;c.lineWidth=3;
  c.beginPath();c.moveTo(bcx-130,48);c.lineTo(bcx-130,146);c.stroke();
  c.beginPath();c.moveTo(bcx+130,48);c.lineTo(bcx+130,146);c.stroke();
  c.strokeStyle=BASE;c.lineWidth=1.5;
  c.beginPath();c.moveTo(bcx-130,48);c.lineTo(bcx-130,146);c.stroke();
  c.beginPath();c.moveTo(bcx+130,48);c.lineTo(bcx+130,146);c.stroke();
  [56,98,138].forEach(yp=>{
    c.strokeStyle=DARK;c.lineWidth=3;c.beginPath();c.moveTo(bcx-120,yp);c.lineTo(bcx+120,yp);c.stroke();
    c.strokeStyle=BASE;c.lineWidth=1.5;c.beginPath();c.moveTo(bcx-120,yp);c.lineTo(bcx+120,yp);c.stroke();
  });
  [[bcx-80,52],[bcx+80,52],[bcx-24,52],[bcx+24,52],[bcx-80,142],[bcx+80,142],[bcx-24,142],[bcx+24,142]].forEach(([ax,ay])=>{
    c.strokeStyle=MID;c.lineWidth=1;
    c.beginPath();c.moveTo(ax-3,ay);c.lineTo(ax+3,ay);c.stroke();
    c.beginPath();c.moveTo(ax,ay-3);c.lineTo(ax,ay+3);c.stroke();
    gDot(ax,ay,1,GLOW,GDIM);
  });

  // 4. FORWARD WEAPONS DECK y=148..182
  c.fillStyle=BASE;c.beginPath();
  c.moveTo(bcx-142,148);c.lineTo(bcx+142,148);c.lineTo(bcx+124,170);c.lineTo(bcx+88,182);
  c.lineTo(bcx-88,182);c.lineTo(bcx-124,170);c.closePath();c.fill();
  c.strokeStyle=PANEL;c.lineWidth=0.8;c.stroke();
  grd(bcx-132,148,264,28,6,2);
  [bcx-88,bcx-44,bcx,bcx+44,bcx+88].forEach((gx,idx)=>{
    const isC=idx===2,hw=isC?32:24;
    pnl(gx-hw/2,152,hw,18,isC?MID:DARK);grd(gx-hw/2,152,hw,18,isC?3:2,2);
    if(isC){[-8,0,8].forEach(xo=>{brl(gx+xo,168,5,22);});gDot(gx,156,3,GLOW,GDIM);}
    else{brl(gx-5,168,4,20);brl(gx+5,168,4,20);gDot(gx,156,2,GLOW,GDIM);}
  });
  [bcx-66,bcx-22,bcx+22,bcx+66].forEach(tx=>{
    c.fillStyle=DARK;c.beginPath();c.arc(tx,160,7,0,6.2832);c.fill();
    c.strokeStyle=BASE;c.lineWidth=1;c.stroke();
    c.fillStyle=MID;c.beginPath();c.arc(tx,160,4,0,6.2832);c.fill();
    gDot(tx,160,2,GLOW,GDIM);brl(tx,166,3,14);
  });

  // 5. BOW y=182..200
  c.fillStyle=DARK;c.beginPath();
  c.moveTo(bcx-88,182);c.lineTo(bcx+88,182);c.lineTo(bcx+52,196);c.lineTo(bcx,200);c.lineTo(bcx-52,196);
  c.closePath();c.fill();c.strokeStyle=PANEL;c.lineWidth=0.7;c.stroke();
  c.fillStyle=VOID;c.beginPath();c.moveTo(bcx-6,188);c.lineTo(bcx+6,188);c.lineTo(bcx+3,199);c.lineTo(bcx-3,199);c.closePath();c.fill();
  gDot(bcx,197,3.5,GLOW,GDIM);
  gDot(bcx-40,190,2,GLOW,GDIM);gDot(bcx+40,190,2,GLOW,GDIM);

  // 6. HIT FLASH
  if(fl){c.fillStyle=`rgba(255,80,60,${flash*0.38})`;c.fillRect(0,0,CW,CH);}
  return cv;
}


const swapRm=(a,i)=>{a[i]=a[a.length-1];a.pop();};

/* ══════════════════════════════════════════════════════════════
   BACKGROUND ENGINE  v7  —  LIVE MOVING STARFIELD
   ─────────────────────────────────────────────────────────────
   A) One baked nebula canvas (drawn with 1 drawImage per frame):
      - Deep space base gradient
      - Galaxy arm diagonal band
      - Galaxy core warm glow
      - 7 richly coloured nebula clouds (normal & boss variants)

   B) 300 live round stars — each scrolls downward at the same
      speed (NO parallax), wraps back to top when off-screen:
        · x, y  — position (y advances each frame by spd)
        · r     — radius  (0.5 → 4.0 px)
        · phase — twinkle oscillator
        · speed — twinkle rate (unique per star)
        · base  — base brightness (0.3 → 1.0)
        · type  — colour tier index
      Colour tiers (5):
        0 cold blue-white   [170,210,255]  45%
        1 warm white        [255,248,220]  22%
        2 gold              [255,215,100]  14%
        3 rose/violet       [220,160,255]  11%
        4 aqua/teal         [100,235,220]   8%
      Bright stars (r > 2.4) get a soft radial bloom halo
      Boss mode: scroll speed ×2.2, faster twinkle, red nebula
══════════════════════════════════════════════════════════════ */
function createBgEngine(W, H) {

  /* ── colour palette [r,g,b] ── */
  const PAL = [
    [170, 210, 255],  // 0 cold blue-white
    [255, 248, 220],  // 1 warm white
    [255, 215, 100],  // 2 gold
    [220, 160, 255],  // 3 rose/violet
    [100, 235, 220],  // 4 aqua/teal
  ];
  /* cumulative distribution for colour selection */
  const CDIST = [0.45, 0.67, 0.81, 0.92, 1.0];

  function pickType() {
    const r = Math.random();
    for (let i = 0; i < CDIST.length; i++) if (r < CDIST[i]) return i;
    return 4;
  }

  /* ── 500 live stars ── */
  const SN = 500;
  const sX   = new Float32Array(SN);
  const sY   = new Float32Array(SN);
  const sR   = new Float32Array(SN);
  const sPH  = new Float32Array(SN);  // twinkle phase
  const sPS  = new Float32Array(SN);  // twinkle phase-speed
  const sBR  = new Float32Array(SN);  // base brightness
  const sTP  = new Uint8Array(SN);    // colour type

  /* size tier probabilities: micro 55%, small 28%, medium 12%, bright 5%
     All radii are kept small for a crisp HD look */
  function pickRadius() {
    const r = Math.random();
    if (r < 0.55) return 0.2 + Math.random() * 0.3;   // micro  0.2–0.5
    if (r < 0.83) return 0.5 + Math.random() * 0.35;  // small  0.5–0.85
    if (r < 0.95) return 0.85 + Math.random() * 0.45; // medium 0.85–1.3
    return 1.3 + Math.random() * 0.55;                 // bright 1.3–1.85
  }

  for (let i = 0; i < SN; i++) {
    sX[i]  = Math.random() * W;
    sY[i]  = Math.random() * H;           // start scattered, not all at top
    sR[i]  = pickRadius();
    sPH[i] = Math.random() * 6.2832;
    sPS[i] = 0.006 + Math.random() * 0.022;
    sBR[i] = 0.30 + Math.random() * 0.70;
    sTP[i] = pickType();
  }

  /* ── bake the static nebula background ── */
  function bake(boss) {
    const cv = mkC(W, H);
    const cx = cv.getContext('2d');

    /* 1. Base space gradient — very dark, not pure black */
    const base = cx.createLinearGradient(0, 0, W * 0.6, H);
    if (boss) {
      base.addColorStop(0, '#0a0005');
      base.addColorStop(0.5, '#060003');
      base.addColorStop(1, '#040008');
    } else {
      base.addColorStop(0, '#00030e');
      base.addColorStop(0.5, '#000208');
      base.addColorStop(1, '#010005');
    }
    cx.fillStyle = base;
    cx.fillRect(0, 0, W, H);

    /* 2. Galaxy arm — diagonal luminous band */
    cx.save();
    cx.translate(W * 0.5, H * 0.5);
    cx.rotate(-0.32);
    const arm = cx.createLinearGradient(0, -H * 0.55, 0, H * 0.55);
    arm.addColorStop(0, 'rgba(0,0,0,0)');
    arm.addColorStop(0.3,  boss ? 'rgba(80,5,5,0.06)'    : 'rgba(20,45,120,0.06)');
    arm.addColorStop(0.5,  boss ? 'rgba(110,8,8,0.10)'   : 'rgba(30,60,150,0.10)');
    arm.addColorStop(0.7,  boss ? 'rgba(80,5,5,0.06)'    : 'rgba(20,45,120,0.06)');
    arm.addColorStop(1, 'rgba(0,0,0,0)');
    cx.fillStyle = arm;
    cx.fillRect(-W, -H * 0.6, W * 2, H * 1.2);
    cx.restore();

    /* 3. Galaxy core glow */
    const gcx = W * 0.40, gcy = H * 0.46;
    const gc = cx.createRadialGradient(gcx, gcy, 0, gcx, gcy, W * 0.50);
    gc.addColorStop(0,   boss ? 'rgba(160,50,20,0.12)' : 'rgba(140,100,40,0.12)');
    gc.addColorStop(0.3, boss ? 'rgba(90,20,30,0.07)'  : 'rgba(70,45,100,0.07)');
    gc.addColorStop(0.7, boss ? 'rgba(30,5,10,0.02)'   : 'rgba(15,20,65,0.02)');
    gc.addColorStop(1, 'rgba(0,0,0,0)');
    cx.fillStyle = gc;
    cx.fillRect(0, 0, W, H);

    /* 4. Nebula clouds — 7 rich, large elliptical volumes */
    const clouds = boss ? [
      { cx: 0.18, cy: 0.22, rx: 0.30, ry: 0.16, h: 0,   s: 0.80, a: 0.16, a2: 0.08 },
      { cx: 0.78, cy: 0.48, rx: 0.24, ry: 0.28, h: 350,  s: 0.75, a: 0.14, a2: 0.07 },
      { cx: 0.52, cy: 0.85, rx: 0.38, ry: 0.12, h: 10,   s: 0.70, a: 0.12, a2: 0.06 },
      { cx: 0.88, cy: 0.14, rx: 0.18, ry: 0.22, h: 340,  s: 0.65, a: 0.10, a2: 0.05 },
      { cx: 0.05, cy: 0.68, rx: 0.22, ry: 0.26, h: 20,   s: 0.60, a: 0.09, a2: 0.04 },
      { cx: 0.44, cy: 0.35, rx: 0.20, ry: 0.18, h: 5,    s: 0.72, a: 0.11, a2: 0.05 },
      { cx: 0.70, cy: 0.80, rx: 0.25, ry: 0.14, h: 355,  s: 0.68, a: 0.09, a2: 0.04 },
    ] : [
      { cx: 0.15, cy: 0.24, rx: 0.30, ry: 0.16, h: 210,  s: 0.72, a: 0.16, a2: 0.08 },
      { cx: 0.76, cy: 0.50, rx: 0.24, ry: 0.28, h: 270,  s: 0.65, a: 0.14, a2: 0.07 },
      { cx: 0.50, cy: 0.84, rx: 0.38, ry: 0.12, h: 195,  s: 0.70, a: 0.12, a2: 0.06 },
      { cx: 0.88, cy: 0.15, rx: 0.18, ry: 0.22, h: 185,  s: 0.60, a: 0.10, a2: 0.05 },
      { cx: 0.06, cy: 0.66, rx: 0.22, ry: 0.26, h: 285,  s: 0.58, a: 0.09, a2: 0.04 },
      { cx: 0.44, cy: 0.36, rx: 0.20, ry: 0.18, h: 162,  s: 0.66, a: 0.11, a2: 0.05 },
      { cx: 0.70, cy: 0.78, rx: 0.25, ry: 0.14, h: 230,  s: 0.62, a: 0.09, a2: 0.04 },
    ];

    clouds.forEach(n => {
      const nx = n.cx * W, ny = n.cy * H, rx = n.rx * W;
      cx.save();
      cx.translate(nx, ny);
      cx.scale(1, (n.ry * H) / rx);
      const sat = (n.s * 100) | 0;
      const g = cx.createRadialGradient(0, 0, 0, 0, 0, rx);
      g.addColorStop(0,    `hsla(${n.h},${sat}%,45%,${n.a})`);
      g.addColorStop(0.35, `hsla(${n.h + 15},${(sat * 0.8) | 0}%,30%,${n.a * 0.55})`);
      g.addColorStop(0.70, `hsla(${n.h + 25},${(sat * 0.6) | 0}%,18%,${n.a2})`);
      g.addColorStop(1, 'rgba(0,0,0,0)');
      cx.fillStyle = g;
      cx.beginPath();
      cx.arc(0, 0, rx, 0, 6.2832);
      cx.fill();
      cx.restore();
    });

    return cv;
  }

  const bgNormal = bake(false);
  const bgBoss   = bake(true);

  /* ── base scroll speed (px per 16.667ms frame) ── */
  const BASE_SPD = 0.35;

  function draw(ctx, dt, bossIntensity) {
    /* d = normalised frame delta (1.0 = 60fps) */
    const d = dt / 16.667;

    /* scroll speed — uniform for ALL stars (no parallax) */
    const spd = BASE_SPD * (1 + bossIntensity * 1.2) * d;

    /* twinkle advancement rate */
    const twRate = 1 + bossIntensity * 1.4;

    /* ── layer 0: baked nebula (1 drawImage) ── */
    ctx.drawImage(bgNormal, 0, 0);
    if (bossIntensity > 0) {
      ctx.globalAlpha = Math.min(1, bossIntensity);
      ctx.drawImage(bgBoss, 0, 0);
      ctx.globalAlpha = 1;
    }

    /* ── layer 1: 300 live round twinkling moving stars ── */
    for (let i = 0; i < SN; i++) {

      /* advance scroll — wrap back to top when off-screen */
      sY[i] += spd;
      if (sY[i] > H + sR[i] * 2) {
        sY[i] = -sR[i] * 2 - Math.random() * 20;
        sX[i] = Math.random() * W;
        /* occasionally re-roll colour/size for variety */
        if (Math.random() < 0.15) {
          sR[i]  = pickRadius();
          sTP[i] = pickType();
          sBR[i] = 0.30 + Math.random() * 0.70;
          sPS[i] = 0.006 + Math.random() * 0.022;
        }
      }

      /* twinkle oscillation */
      sPH[i] += sPS[i] * twRate;
      const tw = 0.45 + 0.55 * (0.5 + 0.5 * Math.sin(sPH[i]));
      const alpha = sBR[i] * tw;
      if (alpha < 0.04) continue;

      const [r, g, b] = PAL[sTP[i]];
      const rad = sR[i];

      /* star body — always round */
      ctx.globalAlpha = alpha;
      ctx.fillStyle = `rgb(${r},${g},${b})`;
      ctx.beginPath();
      ctx.arc(sX[i], sY[i], rad, 0, 6.2832);
      ctx.fill();

      /* subtle bloom halo only on the largest/brightest stars */
      if (rad > 1.1 && tw > 0.6) {
        const bloom = rad * 3.2;
        const bg2 = ctx.createRadialGradient(sX[i], sY[i], rad * 0.5, sX[i], sY[i], bloom);
        bg2.addColorStop(0, `rgba(${r},${g},${b},${alpha * 0.18})`);
        bg2.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = bg2;
        ctx.beginPath();
        ctx.arc(sX[i], sY[i], bloom, 0, 6.2832);
        ctx.fill();
      }
    }

    ctx.globalAlpha = 1;
  }

  return { draw };
}

/* ══════════════════════════════════════════════ */

export default function WarpDrive(){
  const canvasRef   = useRef(null);
  const keysRef     = useRef({});
  const termRef     = useRef(null);
  const introRef    = useRef(null);
  const introSubRef = useRef(null);
  const hudRef      = useRef(null);
  const scoreRef    = useRef(null);
  const fillRef     = useRef(null);
  const mvalRef     = useRef(null);
  const comboRef    = useRef(null);
  const hintRef     = useRef(null);
  const bossAlertRef= useRef(null);
  const bwarnRef    = useRef(null);
  const laserRef    = useRef(null);
  const goRef       = useRef(null);

  useEffect(()=>{
    /* inject CSS once */
    if(!document.getElementById('wd-v5')){
      const s=document.createElement('style');s.id='wd-v5';s.textContent=CSS;document.head.appendChild(s);
    }

    let termActive=false;
    let termCmdFn=null;

    function globalKey(e){
      if(!termActive||e.key!=='Enter')return;
      const inp=termRef.current?.querySelector('.tinp');
      if(!inp||document.activeElement!==inp)return;
      const val=inp.value;
      inp.value='';
      if(termCmdFn)termCmdFn(val);
    }
    window.addEventListener('keydown',globalKey);

    /* ══ TERMINAL ══ */
    function runTerminal(){
      const term=termRef.current;if(!term)return;
      termActive=false;

      term.style.display='flex';
      term.style.transition='none';
      term.style.opacity='1';

      const out=term.querySelector('.tout');
      const iRow=term.querySelector('.tinrow');
      const inp=term.querySelector('.tinp');
      out.innerHTML='';
      iRow.style.display='none';
      inp.value='';

      let launching=false;

      function scrollBot(){term.querySelector('.tscroll').scrollTop=99999;}
      function line(html){const d=document.createElement('div');d.innerHTML=html;out.appendChild(d);scrollBot();}

      let bi=0;
      function nextBoot(){
        if(bi>=BOOT.length){
          iRow.style.display='flex';
          setTimeout(()=>{inp.focus();termActive=true;},60);
          return;
        }
        const e=BOOT[bi++];
        if(e.t==='type'){
          const tmp=document.createElement('div');tmp.innerHTML=e.html;
          const full=tmp.textContent;let ci=0;
          const row=document.createElement('div');out.appendChild(row);
          const cur=document.createElement('span');cur.className='tcur';row.appendChild(cur);
          (function tick(){
            if(ci>full.length){row.innerHTML=e.html;setTimeout(nextBoot,e.d);return;}
            row.textContent=full.slice(0,ci);row.appendChild(cur);ci++;
            scrollBot();setTimeout(tick,22);
          })();
        }else{
          line(e.html);
          if(e.final){nextBoot();return;}
          setTimeout(nextBoot,e.d);
        }
      }
      nextBoot();

      termCmdFn=function cmd(raw){
        const val=raw.trim();if(!val)return;
        line(`<span class="hi">pilot@warp-drive</span><span class="dim">:</span><span class="p2">~/game</span><span class="dim">$</span> <span class="cmd">${val}</span>`);
        const v=val.toLowerCase();

        if(['warp-drive','./warp-drive','sudo warp-drive'].includes(v)){
          if(launching)return;
          launching=true;
          termActive=false;
          iRow.style.display='none';
          let li=0;
          (function nxt(){
            if(li>=LAUNCH.length){doIntro();return;}
            const s=LAUNCH[li++];line(s.html);scrollBot();setTimeout(nxt,s.d);
          })();
          return;
        }

        if(v==='exit'||v==='quit'){
          line('<span class="ok">Goodbye, pilot. Safe travels.</span>');
          line('<span class="dim">Returning to home base...</span>');
          termActive=false;
          setTimeout(()=>{
            try{if(window.history.length>1)window.history.back();else window.location.reload();}
            catch(_){window.location.reload();}
          },900);
          return;
        }

        if(v==='help'){
          line('<span class="dim">Commands:</span>');
          line('  <span class="ok">sudo warp-drive</span>   launch the game');
          line('  <span class="ok">cat README</span>        mission briefing');
          line('  <span class="ok">neofetch</span>          system info');
          line('  <span class="ok">clear</span>             clear terminal');
          line('  <span class="ok">exit</span>              return to home page');
        }else if(v==='cat readme'||v==='cat readme.md'){
          line('<span class="dim">────────────────────────────────────</span>');
          line('  <span class="ok">WARP DRIVE</span> — SECTOR DEFENSE v5');
          line('<span class="dim">────────────────────────────────────</span>');
          line('  Target      : <span class="ok">800 pts</span>');
          line('  Boss spawns : <span class="warn">300 pts</span>  (Aries Battlecruiser)');
          line('  Laser ready : <span class="warn">500 pts</span>  · Boss killable: <span class="warn">550 pts</span>');
        }else if(v==='clear'){out.innerHTML='';}
        else if(v==='whoami'){line('pilot');}
        else if(v==='pwd'){line('/home/pilot');}
        else if(v==='neofetch'){
          const now=new Date(),pad2=n=>String(n).padStart(2,'0');
          line('<span class="ok">     .:::::::.    </span>  <span style="color:#ccc;font-weight:700">pilot</span><span class="dim">@</span><span style="color:#ccc;font-weight:700">warp-drive</span>');
          line('<span class="ok">  .::::::::::::.  </span>  <span class="dim">OS:</span>  WarpOS 5.15 x86_64');
          line('<span class="ok">  :::::<span class="warn">((()))</span>::::: </span>  <span class="dim">Up:</span>  '+Math.floor(performance.now()/60000)+'m');
          line('<span class="ok">     .:::::::.    </span>  <span class="dim">Time:</span> '+pad2(now.getHours())+':'+pad2(now.getMinutes()));
          line('<span style="background:#f00;color:#f00">███</span><span style="background:#f90;color:#f90">███</span><span style="background:#ff0;color:#ff0">███</span><span style="background:#0f0;color:#0f0">███</span><span style="background:#00f;color:#00f">███</span><span style="background:#0ff;color:#0ff">███</span><span style="background:#fff;color:#fff">███</span>');
        }else{
          line(`<span class="err">bash: ${val}: command not found</span>`);
          line('<span class="dim">Try <span class="ok">sudo warp-drive</span>, <span class="ok">help</span>, or <span class="ok">exit</span></span>');
        }
        scrollBot();
      };
    }

    /* ══ WARP INTRO ══ */
    function doIntro(){
      const term=termRef.current,intro=introRef.current;
      if(term){term.style.transition='opacity .28s';term.style.opacity='0';}
      setTimeout(()=>{
        if(term)term.style.display='none';
        if(!intro)return;
        intro.classList.add('on');
        const subs=['WARP SPEED ENGAGED','ENTERING HYPERSPACE','DESTINATION: SECTOR-7'];
        [550,1250,2050].forEach((t,i)=>setTimeout(()=>{if(introSubRef.current)introSubRef.current.textContent=subs[i];},t));
        setTimeout(()=>{
          intro.style.transition='opacity .3s';intro.style.opacity='0';
          setTimeout(()=>{intro.classList.remove('on');intro.style.opacity='';runGame();},320);
        },2900);
      },290);
    }

    /* ══ GAME ══ */
    let rafId=null;
    let gameKeyDn=null,gameKeyUp=null,gameResize=null;

    function stopGame(){
      if(rafId!=null){cancelAnimationFrame(rafId);rafId=null;}
      if(gameKeyDn){window.removeEventListener('keydown',gameKeyDn);gameKeyDn=null;}
      if(gameKeyUp){window.removeEventListener('keyup',gameKeyUp);gameKeyUp=null;}
      if(gameResize){window.removeEventListener('resize',gameResize);gameResize=null;}
    }

    function runGame(){
      stopGame();

      const canvas=canvasRef.current;if(!canvas)return;
      canvas.style.display='block';
      if(hudRef.current)hudRef.current.style.display='block';

      const ctx=canvas.getContext('2d',{alpha:false});
      let W=canvas.width=window.innerWidth;
      let H=canvas.height=window.innerHeight;

      let bg=createBgEngine(W,H);

      function resize(){
        W=canvas.width=window.innerWidth;
        H=canvas.height=window.innerHeight;
        bg=createBgEngine(W,H);
        gs.p.x=Math.min(gs.p.x,W-gs.p.w);
        gs.p.y=Math.min(gs.p.y,H-gs.p.h);
      }
      gameResize=resize;
      window.addEventListener('resize',gameResize);

      /* sprites */
      const SPAD=12,PW=50,PH=50;
      let spNorm=buildPlayer(false),spBoss=buildPlayer(true);
      let bsNN=buildBoss(false,0),bsEN=buildBoss(true,0),bsNF=buildBoss(false,.85),bsEF=buildBoss(true,.85);
      const getBoss=(enr,fl)=>fl>.25?(enr?bsEF:bsNF):(enr?bsEN:bsNN);

      /* boss ship external image — loaded once, used if path is set */
      let bossImg=null,bossImgReady=false;
      if(BOSS_SHIP_IMG_PATH){
        bossImg=new Image();
        bossImg.onload=()=>{bossImgReady=true;};
        bossImg.onerror=()=>{bossImg=null;};
        bossImg.src=BOSS_SHIP_IMG_PATH;
      }

      /* typed-array pools */
      const POOL=512;
      const pX=new Float32Array(POOL),pY=new Float32Array(POOL);
      const pVX=new Float32Array(POOL),pVY=new Float32Array(POOL);
      const pL=new Float32Array(POOL),pSz=new Float32Array(POOL);
      const pR=new Uint8Array(POOL),pG=new Uint8Array(POOL),pBc=new Uint8Array(POOL),pAc=new Uint8Array(POOL);
      let pHead=0;
      function spawnP(x,y,vx,vy,r,g,b,sz){
        for(let i=0;i<POOL;i++){const idx=(pHead+i)%POOL;if(!pAc[idx]){pAc[idx]=1;pX[idx]=x;pY[idx]=y;pVX[idx]=vx;pVY[idx]=vy;pL[idx]=1;pSz[idx]=sz;pR[idx]=r;pG[idx]=g;pBc[idx]=b;pHead=(idx+1)%POOL;return;}}
      }
      function boom(x,y,n,r,g,b){for(let i=0;i<n;i++){const a=Math.random()*6.2832,s=1.2+Math.random()*5.5;spawnP(x,y,Math.cos(a)*s,Math.sin(a)*s,r,g,b,Math.random()*3.2+.7);}}

      const FN=12;
      const fnX=new Float32Array(FN),fnY=new Float32Array(FN),fnL=new Float32Array(FN);
      const fnT=Array(FN).fill(''),fnC=Array(FN).fill('#fff');let fnH=0;
      const addFN=(x,y,t,c)=>{fnX[fnH]=x;fnY[fnH]=y;fnL[fnH]=1;fnT[fnH]=t;fnC[fnH]=c;fnH=(fnH+1)%FN;};

      const MX=new Float32Array(8),MY=new Float32Array(8),ML=new Float32Array(8);let mHead=0;
      const addMF=(x,y,l)=>{MX[mHead]=x;MY[mHead]=y;ML[mHead]=l;mHead=(mHead+1)%8;};

      const SW=12;
      const swX=new Float32Array(SW),swY=new Float32Array(SW),swR2=new Float32Array(SW),swL=new Float32Array(SW);
      const swC=Array(SW).fill('#fff');let swH=0;
      const addSW=(x,y,c)=>{swX[swH]=x;swY[swH]=y;swR2[swH]=3;swL[swH]=1;swC[swH]=c;swH=(swH+1)%SW;};

      let scFlash=0,flashRed=true,laserP=0;
      let lastSc=-1,lastCo=-1;

      function hudUp(){
        const sc=gs.score;
        if(sc!==lastSc){
          lastSc=sc;
          if(scoreRef.current)scoreRef.current.textContent='SCORE: '+sc;
          const pct=Math.min(sc/800*100,100);
          if(fillRef.current){
            fillRef.current.style.width=pct+'%';
            fillRef.current.style.background=
              sc>=800?'linear-gradient(90deg,#238636,#3fb950)':
              sc>=600?'linear-gradient(90deg,#9e6a03,#d29922)':
              sc>=300?'linear-gradient(90deg,#b36200,#e3b341)':
              'linear-gradient(90deg,#1f6feb,#58a6ff)';
          }
          if(mvalRef.current)mvalRef.current.textContent=Math.min(sc,800)+' / 800';
        }
        if(gs.combo!==lastCo){
          lastCo=gs.combo;
          if(comboRef.current){
            if(gs.combo>1){comboRef.current.style.display='block';comboRef.current.textContent='COMBO ×'+gs.combo;}
            else comboRef.current.style.display='none';
          }
        }
      }

      const gs={
        score:0,combo:0,lastKill:0,active:true,
        bossMode:false,bossShip:null,bossSpawned:false,
        bossAppearing:false,bossP:0,
        bossDying:false,bossDp:0,
        bossEnraged:false,bossDir:1,
        laserOn:false,laserOnce:false,
        p:{x:W/2-PW/2,y:H-120,w:PW,h:PH,spd:9,vx:0,vy:0,t:0},
        bullets:[],eB:[],enemies:[],holes:[],
        spRate:900,lastSpawn:0,lastShot:0,lastHole:0,
      };

      function die(byPlayer){
        if(!gs.active)return;
        gs.active=false;scFlash=1.4;flashRed=byPlayer;
        setTimeout(()=>{
          const go=goRef.current;if(!go)return;
          const win=!byPlayer&&gs.score>=800;
          go.className='go'+(win?'':' fail');
          go.querySelector('.goeye').textContent=win?'SECTOR DEFENSE COMPLETE':'MISSION REPORT';
          go.querySelector('.goh').textContent=win?'MISSION COMPLETE':'MISSION FAILED';
          go.querySelector('.gosc').textContent='FINAL SCORE: '+gs.score+' / 800';
          go.style.display='block';
          go.querySelector('.gobtn').onclick=()=>{
            go.style.display='none';
            canvas.style.display='none';
            if(hudRef.current)hudRef.current.style.display='none';
            if(bossAlertRef.current){bossAlertRef.current.style.display='none';bossAlertRef.current.textContent='⚠ BOSS BATTLE ⚠';}
            if(hintRef.current)hintRef.current.style.display='block';
            if(comboRef.current)comboRef.current.style.display='none';
            lastSc=-1;lastCo=-1;
            stopGame();
            runTerminal();
          };
        },byPlayer?340:400);
      }

      function spawnEnemy(now){
        if(now-gs.lastSpawn<gs.spRate)return;gs.lastSpawn=now;
        const big=gs.score>=200,sz=big?58:42;
        gs.enemies.push({x:Math.random()*(W-sz),y:-sz,w:sz,h:sz,spd:1.8+Math.random()*2.2,wob:Math.random()*6.2832,wobSpd:.043+Math.random()*.027,lastShot:0,shootInt:1400+Math.random()*1100,hp:big?3:1,maxHp:big?3:1,big,pulled:false,pullSc:1,sa:0});
        if(gs.score>=200&&gs.spRate>355)gs.spRate=Math.max(355,gs.spRate-3.5);
        else if(gs.spRate>465)gs.spRate-=1.2;
      }
      function spawnHole(now){
        if(gs.score-gs.lastHole<70||gs.score<70)return;gs.lastHole=gs.score;
        const sz=56+Math.random()*20;
        gs.holes.push({x:Math.random()*(W-sz),y:-sz,sz,spd:.95+Math.random()*.7,pullR:sz*2.4,rot:0,sa:0,t:0});
      }

      function shoot(now){
        if(now-gs.lastShot<138)return;gs.lastShot=now;
        const p=gs.p;
        addMF(p.x+p.w/2,p.y,1);addMF(p.x+p.w*.28,p.y+p.h*.1,.58);addMF(p.x+p.w*.72,p.y+p.h*.1,.58);
        if(gs.bossMode&&gs.bossShip&&!gs.bossAppearing&&!gs.bossDying){
          const bs=gs.bossShip,bCX=bs.x+bs.w/2,bCY=bs.y+bs.h/2,pCX=p.x+p.w/2,pCY=p.y+p.h/2;
          const ang=Math.atan2(bCY-pCY,bCX-pCX);
          if(gs.score>=500){
            if(!gs.laserOn){gs.laserOn=true;if(!gs.laserOnce){gs.laserOnce=true;const ln=laserRef.current;if(ln){ln.style.display='block';setTimeout(()=>{ln.style.display='none';},2000);}bs.baseInt=Math.max(680,bs.baseInt*.6);bs.sInt=bs.baseInt;bs.spreadInt=Math.max(1500,bs.spreadInt*.6);}}
            gs.bullets.push({x:p.x+p.w/2-2.5,y:p.y,tX:bCX,tY:bCY,w:5,laser:true,life:.2});
          }
          const sp2=12.5;
          gs.bullets.push({x:p.x+p.w*.25,y:p.y-8,w:6,h:15,sx:Math.cos(ang)*sp2,sy:Math.sin(ang)*sp2,aimed:true});
          gs.bullets.push({x:p.x+p.w*.75,y:p.y-8,w:6,h:15,sx:Math.cos(ang)*sp2,sy:Math.sin(ang)*sp2,aimed:true});
        }else if(!gs.bossMode){
          gs.bullets.push({x:p.x+p.w*.28-2,y:p.y-8,w:4,h:16,spd:15});
          gs.bullets.push({x:p.x+p.w*.72-2,y:p.y-8,w:4,h:16,spd:15});
        }
      }

      function bossShoot(now){
        const bs=gs.bossShip;if(!bs||gs.bossAppearing||gs.bossDying)return;
        const p=gs.p,pCX=p.x+p.w/2,bCX=bs.x+bs.w/2;
        const sm=(gs.bossEnraged?.64:1)*(gs.laserOn?.72:1);
        if(now-bs.lastShot>=bs.sInt*sm){
          bs.lastShot=now;
          const num=gs.bossEnraged?4:Math.abs(pCX-bCX)<bs.w*.35?3:2;
          const used=[];
          for(let i=0;i<num;i++){const t=bs.turrets[Math.floor(Math.random()*bs.turrets.length)];if(!used.includes(t))used.push(t);}
          used.forEach(t=>{
            const tx=bCX+t.x,ty=bs.y+t.y,a=Math.atan2(p.y+p.h/2-ty,pCX-tx),s=(gs.bossEnraged?6.2:5.4)*(gs.laserOn?1.1:1);
            gs.eB.push({x:tx-4,y:ty,w:8,h:20,sx:Math.cos(a)*s,sy:Math.sin(a)*s,boss:true,tr:new Float32Array(10),trH:0,trLen:0});
          });
        }
        const ss=(gs.bossEnraged?.54:1)*(gs.laserOn?.78:1);
        if(now-bs.lastSpread>=bs.spreadInt*ss){
          bs.lastSpread=now;
          bs.wings.forEach(t=>{
            const tx=bCX+t.x,ty=bs.y+t.y,sc2=gs.bossEnraged?7:5,sa=gs.bossEnraged?.3:.24,s=(gs.bossEnraged?4.5:3.8)*(gs.laserOn?1.1:1);
            for(let i=-Math.floor(sc2/2);i<=Math.floor(sc2/2);i++)
              gs.eB.push({x:tx-3,y:ty,w:5,h:12,sx:Math.cos(Math.PI/2+i*sa)*s,sy:Math.sin(Math.PI/2+i*sa)*s,boss:true,tr:new Float32Array(10),trH:0,trLen:0});
          });
        }
      }

      function spawnBoss(){
        const bw=bwarnRef.current;if(bw)bw.classList.add('on');
        setTimeout(()=>{
          if(bwarnRef.current)bwarnRef.current.classList.remove('on');
          gs.enemies.length=0;gs.holes.length=0;gs.eB.length=0;
          gs.bossMode=true;gs.bossAppearing=true;gs.bossP=0;
          spBoss=buildPlayer(true);
          gs.p.x=W/2-PW/2;gs.p.y=H-120;
          if(hintRef.current)hintRef.current.style.display='none';
          if(bossAlertRef.current)bossAlertRef.current.style.display='block';
          gs.bossShip={x:W/2-150,y:-260,targetY:80,baseX:W/2-150,w:300,h:180,hp:500,maxHp:500,lastShot:0,baseInt:1700,sInt:1700,spreadInt:3000,lastSpread:0,hitFlash:0,turrets:[{x:-88,y:60},{x:-44,y:68},{x:0,y:72},{x:44,y:68},{x:88,y:60}],wings:[{x:-135,y:84},{x:135,y:84}]};
        },3300);
      }

      const FIXED=1000/60,MAXDT=50;
      let acc=0,gt=0;

      function update(dt,now){
        if(!gs.active)return;
        const d=dt/16.667;
        if(now-gs.lastKill>2000&&gs.combo>0)gs.combo=0;
        if(gs.score>=300&&!gs.bossMode&&!gs.bossShip&&!gs.bossSpawned){gs.bossSpawned=true;spawnBoss();}
        if(gs.bossAppearing&&gs.bossShip){
          gs.bossP=Math.min(1,gs.bossP+.012*d);
          const ep=gs.bossP<.5?2*gs.bossP*gs.bossP:1-Math.pow(-2*gs.bossP+2,2)/2;
          gs.bossShip.y=-gs.bossShip.h-80+(gs.bossShip.targetY+gs.bossShip.h+80)*ep;
          if(gs.bossP>=1){gs.bossAppearing=false;gs.bossShip.y=gs.bossShip.targetY;gs.bossShip.baseX=gs.bossShip.x;}
        }
        if(gs.bossMode&&gs.bossShip&&!gs.bossAppearing&&!gs.bossDying&&gs.bossEnraged){
          gs.bossShip.x+=gs.bossDir*2.3*d;
          if(gs.bossShip.x<gs.bossShip.baseX-185){gs.bossShip.x=gs.bossShip.baseX-185;gs.bossDir=1;}
          else if(gs.bossShip.x>gs.bossShip.baseX+185){gs.bossShip.x=gs.bossShip.baseX+185;gs.bossDir=-1;}
        }
        if(gs.bossDying&&gs.bossShip){
          gs.bossDp=Math.min(1,gs.bossDp+.007*d);
          gs.bossShip.x=gs.bossShip.baseX+(Math.random()-.5)*16*(1-gs.bossDp);
          if(Math.random()<.15){boom(gs.bossShip.x+Math.random()*gs.bossShip.w,gs.bossShip.y+Math.random()*gs.bossShip.h,6,255,60,10);if(Math.random()<.26)addSW(gs.bossShip.x+gs.bossShip.w/2,gs.bossShip.y+gs.bossShip.h/2,'#ff3300');}
          if(gs.bossDp>=1){
            const bx=gs.bossShip.x+gs.bossShip.w/2,by=gs.bossShip.y+gs.bossShip.h/2;
            boom(bx,by,42,255,48,0);boom(bx,by,25,255,175,35);addSW(bx,by,'#ff7700');addSW(bx,by,'#ffbb00');
            scFlash=1.2;flashRed=false;gs.score+=250;addFN(bx,by-40,'+250 BONUS','#e3b341');
            gs.active=false;setTimeout(()=>die(false),50);
          }
        }
        if(gs.bossMode&&gs.bossShip&&!gs.bossAppearing&&!gs.bossDying){
          if(gs.bossShip.hp/gs.bossShip.maxHp<=.5&&!gs.bossEnraged){
            gs.bossEnraged=true;scFlash=.8;flashRed=true;
            if(bossAlertRef.current)bossAlertRef.current.textContent='⚠ ENRAGED MODE ⚠';
            addSW(gs.bossShip.x+gs.bossShip.w/2,gs.bossShip.y+gs.bossShip.h/2,'#ff0000');
          }
          gs.bossShip.sInt=Math.max(880,gs.bossShip.baseInt-Math.floor((gs.score-300)/10)*36);
          if(gs.bossShip.hitFlash>0)gs.bossShip.hitFlash=Math.max(0,gs.bossShip.hitFlash-.065*d);
        }
        scFlash=Math.max(0,scFlash-.05*d);laserP+=.1*d;
        let sm=1;
        if(!gs.bossMode){if(gs.score>=250)sm=2;else if(gs.score>=200)sm=1.75;else if(gs.score>=150)sm=1.48;else if(gs.score>=100)sm=1.28;else if(gs.score>=50)sm=1.13;}
        const p=gs.p,fr=.88;
        if(keysRef.current['ArrowLeft']||keysRef.current['KeyA'])p.vx-=d;
        if(keysRef.current['ArrowRight']||keysRef.current['KeyD'])p.vx+=d;
        if(!gs.bossMode){
          if(keysRef.current['ArrowUp']||keysRef.current['KeyW'])p.vy-=d;
          if(keysRef.current['ArrowDown']||keysRef.current['KeyS'])p.vy+=d;
        }else{p.vy*=Math.pow(fr,d);p.y=H-120;}
        p.vx*=Math.pow(fr,d);p.vy*=Math.pow(fr,d);
        const mv=p.spd*1.5,vel=Math.hypot(p.vx,p.vy);
        if(vel>mv){const inv=mv/vel;p.vx*=inv;p.vy*=inv;}
        p.x=Math.max(0,Math.min(W-p.w,p.x+p.vx*d));
        p.y=Math.max(0,Math.min(H-p.h,p.y+p.vy*d));
        p.t+=d;
        if(Math.random()<.36)spawnP(p.x+p.w/2+(Math.random()-.5)*8,p.y+p.h*.72,p.vx*.06+(Math.random()-.5)*.22,1.3+Math.random()*1.7,0,168+(Math.random()*87|0),255,Math.random()*1.7+.5);
        shoot(now);
        for(let i=gs.bullets.length-1;i>=0;i--){
          const b=gs.bullets[i];
          if(b.laser){if(gs.bossShip){b.tX=gs.bossShip.x+gs.bossShip.w/2;b.tY=gs.bossShip.y+gs.bossShip.h/2;b.x=p.x+p.w/2-2.5;}b.life-=.032*d;if(b.life<=0)swapRm(gs.bullets,i);}
          else if(b.aimed){b.x+=b.sx*d;b.y+=b.sy*d;if(b.y<-b.h||b.x<-b.w||b.x>W+b.w)swapRm(gs.bullets,i);}
          else{b.y-=b.spd*sm*d;if(b.y<-b.h)swapRm(gs.bullets,i);}
        }
        for(let i=gs.eB.length-1;i>=0;i--){
          const eb=gs.eB[i];
          const sl=eb.trH*2;eb.tr[sl]=eb.x+eb.w/2;eb.tr[sl+1]=eb.y;eb.trH=(eb.trH+1)%5;if(eb.trLen<5)eb.trLen++;
          if(eb.boss){eb.x+=eb.sx*d;eb.y+=eb.sy*d;}else eb.y+=eb.spd*sm*d;
          if(eb.y>H+30||eb.x<-60||eb.x>W+60){swapRm(gs.eB,i);continue;}
          if(p.x+5<eb.x+eb.w&&p.x+p.w-5>eb.x&&p.y+5<eb.y+eb.h&&p.y+p.h-5>eb.y)die(true);
        }
        if(gs.bossMode&&gs.bossShip&&!gs.bossDying){
          bossShoot(now);
          if(!gs.bossAppearing){
            for(let i=0;i<gs.bullets.length;i++){const b=gs.bullets[i];if(b.laser&&b.life>0){gs.bossShip.hp-=.95*d;gs.bossShip.hitFlash=Math.min(1,gs.bossShip.hitFlash+.09);if(gs.bossShip.hp<=0&&gs.score>=550){gs.bossDying=true;gs.bossDp=0;gs.eB.length=0;}else if(gs.bossShip.hp<=0)gs.bossShip.hp=10;break;}}
            for(let i=gs.bullets.length-1;i>=0;i--){const b=gs.bullets[i];if(b.aimed&&!b.laser&&b.x<gs.bossShip.x+gs.bossShip.w&&b.x+b.w>gs.bossShip.x&&b.y<gs.bossShip.y+gs.bossShip.h&&b.y+b.h>gs.bossShip.y){gs.bossShip.hp-=.55;gs.bossShip.hitFlash=Math.min(1,gs.bossShip.hitFlash+.22);swapRm(gs.bullets,i);gs.score+=1;addFN(b.x,b.y,'+1','#58a6ff');boom(b.x,b.y,4,255,120,15);if(gs.bossShip.hp<=0&&gs.score>=550){gs.bossDying=true;gs.bossDp=0;gs.eB.length=0;}else if(gs.bossShip.hp<=0)gs.bossShip.hp=10;}}
          }
        }
        outer:for(let i=gs.bullets.length-1;i>=0;i--){const pb=gs.bullets[i];if(pb.laser)continue;for(let j=gs.eB.length-1;j>=0;j--){const eb=gs.eB[j];if(pb.x<eb.x+eb.w&&pb.x+pb.w>eb.x&&pb.y<eb.y+eb.h&&pb.y+pb.h>eb.y){swapRm(gs.bullets,i);swapRm(gs.eB,j);boom(pb.x,pb.y,4,255,235,150);addSW(pb.x,pb.y,'#fff');break outer;}}}
        if(!gs.bossMode){spawnEnemy(now);spawnHole(now);}
        for(let i=gs.holes.length-1;i>=0;i--){
          const h=gs.holes[i];h.y+=h.spd*sm*d;h.rot+=.008*d;h.t=(h.t||0)+d;if(h.sa<1)h.sa=Math.min(1,h.sa+.034*d);
          if(h.y>H+h.sz){swapRm(gs.holes,i);continue;}
          const hcx=h.x+h.sz/2,hcy=h.y+h.sz/2,pcx=p.x+p.w/2,pcy=p.y+p.h/2;
          const dist=Math.hypot(pcx-hcx,pcy-hcy);
          if(dist<h.pullR){const pull=(1-dist/h.pullR)*.46*d,a=Math.atan2(hcy-pcy,hcx-pcx);p.x+=Math.cos(a)*pull;p.y+=Math.sin(a)*pull;}
          if(dist<h.sz*.42)die(true);
          for(let j=gs.enemies.length-1;j>=0;j--){const e=gs.enemies[j],ex=e.x+e.w/2,ey=e.y+e.h/2,ed=Math.hypot(ex-hcx,ey-hcy);if(ed<h.pullR){const p2=(1-ed/h.pullR)*1.35*d,a2=Math.atan2(hcy-ey,hcx-ex);e.x+=Math.cos(a2)*p2;e.y+=Math.sin(a2)*p2;if(ed<h.sz*.84){e.pulled=true;e.pullSc=Math.max(.04,ed/(h.sz*.84));}}if(ed<h.sz*.4)swapRm(gs.enemies,j);}
        }
        if(!gs.bossMode){
          const wm=1+gs.score/480;
          for(let i=gs.enemies.length-1;i>=0;i--){
            const e=gs.enemies[i];
            if(e.sa<1)e.sa=Math.min(1,e.sa+.05*d);
            e.y+=e.spd*sm*d;e.wob+=e.wobSpd*wm*d;e.x+=Math.sin(e.wob)*1.3*d;
            if(now-e.lastShot>e.shootInt&&e.y>50&&e.y<H-165){if(Math.random()>.3)gs.eB.push({x:e.x+e.w/2-2.5,y:e.y+e.h,w:5,h:12,spd:3.7,boss:false,tr:new Float32Array(10),trH:0,trLen:0});e.lastShot=now;}
            if(e.y>H+e.h){swapRm(gs.enemies,i);continue;}
            let hit=false;
            for(let j=gs.bullets.length-1;j>=0;j--){const b=gs.bullets[j];if(b.laser)continue;if(b.x<e.x+e.w&&b.x+b.w>e.x&&b.y<e.y+e.h&&b.y+b.h>e.y){e.hp--;swapRm(gs.bullets,j);boom(b.x,b.y,4,255,55,55);if(e.hp<=0){const ex=e.x+e.w/2,ey=e.y+e.h/2;boom(ex,ey,13,255,85,18);boom(ex,ey,5,255,175,55);addSW(ex,ey,'#ff5500');swapRm(gs.enemies,i);gs.combo=now-gs.lastKill<2000?gs.combo+1:1;gs.lastKill=now;const pts=(e.big?25:10)*gs.combo;gs.score+=pts;addFN(ex,ey-16,'+'+pts,pts>=20?'#e3b341':'#58a6ff');hit=true;}break;}}
            if(!hit&&p.x+7<e.x+e.w&&p.x+p.w-7>e.x&&p.y+7<e.y+e.h&&p.y+p.h-7>e.y)die(true);
          }
        }
        for(let i=0;i<FN;i++)if(fnL[i]>0){fnY[i]-=1.2*d;fnL[i]=Math.max(0,fnL[i]-.02*d);}
        for(let i=0;i<8;i++)if(ML[i]>0)ML[i]=Math.max(0,ML[i]-.12*d);
        for(let i=0;i<SW;i++)if(swL[i]>0){swR2[i]+=3.3*d;swL[i]=Math.max(0,swL[i]-.05*d);}
        const pfr=Math.pow(.97,d),pdc=.018*d;
        for(let i=0;i<POOL;i++){if(!pAc[i])continue;pX[i]+=pVX[i]*d;pY[i]+=pVY[i]*d;pVX[i]*=pfr;pVY[i]*=pfr;pL[i]-=pdc;if(pL[i]<=0)pAc[i]=0;}
        hudUp();
      }

      function draw(dt){
        const bossIntensity=gs.bossEnraged?.65:gs.bossMode?.35:0;
        bg.draw(ctx, dt, bossIntensity);

        for(let i=0;i<SW;i++){if(swL[i]<=0)continue;ctx.globalAlpha=swL[i]*.38;ctx.strokeStyle=swC[i];ctx.lineWidth=1.5*swL[i];ctx.beginPath();ctx.arc(swX[i],swY[i],swR2[i],0,6.2832);ctx.stroke();}
        ctx.globalAlpha=1;
        for(let i=0;i<POOL;i++){if(!pAc[i])continue;ctx.globalAlpha=pL[i]*pL[i];ctx.fillStyle=`rgb(${pR[i]},${pG[i]},${pBc[i]})`;ctx.fillRect(pX[i],pY[i],pSz[i],pSz[i]);}
        ctx.globalAlpha=1;
        for(let hi=0;hi<gs.holes.length;hi++){
          const h=gs.holes[hi],hcx=h.x+h.sz/2,hcy=h.y+h.sz/2;
          const R=h.sz*0.36;   // photon ring radius — smaller
          const EV=R*0.60;     // event horizon
          const pulse=0.97+0.03*Math.sin((h.t||0)*0.04);

          ctx.save();ctx.translate(hcx,hcy);ctx.scale(h.sa,h.sa);

          // ── outer ambient glow (kept tight) ──
          {const og=ctx.createRadialGradient(0,0,R*0.9,0,0,R*2.6);
          og.addColorStop(0,'rgba(210,195,155,0.10)');
          og.addColorStop(0.5,'rgba(160,140,90,0.04)');
          og.addColorStop(1,'rgba(0,0,0,0)');
          ctx.fillStyle=og;ctx.beginPath();ctx.arc(0,0,R*2.6,0,6.2832);ctx.fill();}

          // ── accretion disk arms (horizontal stretched ellipse) ──
          ctx.save();
          ctx.scale(1,0.14);
          const diskLen=R*3.6;
          // soft outer glow of disk
          const dg=ctx.createLinearGradient(-diskLen,0,diskLen,0);
          dg.addColorStop(0,'rgba(0,0,0,0)');
          dg.addColorStop(0.18,'rgba(160,120,60,0.18)');
          dg.addColorStop(0.38,'rgba(230,200,130,0.60)');
          dg.addColorStop(0.47,'rgba(255,240,190,0.88)');
          dg.addColorStop(0.5,'rgba(255,255,230,1)');
          dg.addColorStop(0.53,'rgba(255,240,190,0.88)');
          dg.addColorStop(0.62,'rgba(230,200,130,0.60)');
          dg.addColorStop(0.82,'rgba(160,120,60,0.18)');
          dg.addColorStop(1,'rgba(0,0,0,0)');
          ctx.fillStyle=dg;ctx.fillRect(-diskLen,-R*1.6,diskLen*2,R*3.2);
          // sharp bright centerline streak
          const cl=ctx.createLinearGradient(-diskLen,0,diskLen,0);
          cl.addColorStop(0,'rgba(0,0,0,0)');
          cl.addColorStop(0.25,'rgba(255,235,170,0.25)');
          cl.addColorStop(0.44,'rgba(255,252,220,0.85)');
          cl.addColorStop(0.5,'rgba(255,255,255,1)');
          cl.addColorStop(0.56,'rgba(255,252,220,0.85)');
          cl.addColorStop(0.75,'rgba(255,235,170,0.25)');
          cl.addColorStop(1,'rgba(0,0,0,0)');
          ctx.fillStyle=cl;ctx.fillRect(-diskLen,-R*0.22,diskLen*2,R*0.44);
          ctx.restore();

          // ── photon ring — drawn as crisp arc strokes, not filled radial ──
          // layer 1: wide soft warm halo ring
          ctx.strokeStyle='rgba(240,225,185,0.28)';
          ctx.lineWidth=R*0.38;
          ctx.beginPath();ctx.arc(0,0,R,0,6.2832);ctx.stroke();
          // layer 2: medium warm ring
          ctx.strokeStyle='rgba(255,245,210,0.55)';
          ctx.lineWidth=R*0.18;
          ctx.beginPath();ctx.arc(0,0,R,0,6.2832);ctx.stroke();
          // layer 3: sharp bright inner edge
          ctx.strokeStyle='rgba(255,255,240,0.92)';
          ctx.lineWidth=R*0.065;
          ctx.beginPath();ctx.arc(0,0,R,0,6.2832);ctx.stroke();
          // layer 4: very thin hard white ring at exact photon sphere
          ctx.strokeStyle='rgba(255,255,255,1)';
          ctx.lineWidth=R*0.028;
          ctx.beginPath();ctx.arc(0,0,R,0,6.2832);ctx.stroke();

          // ── gravitational lensing — brighter top arc ──
          ctx.strokeStyle='rgba(255,252,235,0.62)';
          ctx.lineWidth=R*0.09;
          ctx.beginPath();ctx.arc(0,0,R,Math.PI*1.08,Math.PI*1.92);ctx.stroke();

          // ── shadow band just inside the ring ──
          const sr=ctx.createRadialGradient(0,0,EV,0,0,R*0.88);
          sr.addColorStop(0,'rgba(2,3,8,1)');
          sr.addColorStop(0.7,'rgba(4,6,14,0.8)');
          sr.addColorStop(1,'rgba(0,0,0,0)');
          ctx.fillStyle=sr;ctx.beginPath();ctx.arc(0,0,R*0.88,0,6.2832);ctx.fill();

          // ── event horizon (pure black) ──
          ctx.fillStyle='#000';ctx.beginPath();ctx.arc(0,0,EV*pulse,0,6.2832);ctx.fill();

          ctx.restore();
        }
        ctx.lineWidth=1.2;
        for(let i=0;i<gs.eB.length;i++){const eb=gs.eB[i];if(eb.trLen<2)continue;ctx.globalAlpha=eb.boss?.14:.10;ctx.strokeStyle=eb.boss?'#f85149':'#ff6050';ctx.beginPath();let st=false;for(let k=0;k<eb.trLen;k++){const sl=((eb.trH-eb.trLen+k+5)%5)*2;if(!st){ctx.moveTo(eb.tr[sl],eb.tr[sl+1]);st=true;}else ctx.lineTo(eb.tr[sl],eb.tr[sl+1]);}ctx.stroke();}
        ctx.globalAlpha=1;
        ctx.shadowBlur=6;ctx.shadowColor='#f85149';
        for(let i=0;i<gs.eB.length;i++){const eb=gs.eB[i];ctx.fillStyle=eb.boss?'#f85149':'#ff5545';ctx.fillRect(eb.x,eb.y,eb.w,eb.h);ctx.fillStyle=`rgba(255,200,200,${eb.boss?.62:.42})`;if(eb.boss)ctx.fillRect(eb.x+2,eb.y+5,eb.w-4,eb.h-10);else ctx.fillRect(eb.x+1,eb.y+2,eb.w-2,3);}
        ctx.shadowBlur=0;
        ctx.shadowBlur=5;ctx.shadowColor='#58a6ff';
        for(let i=0;i<gs.bullets.length;i++){const b=gs.bullets[i];if(b.laser||b.aimed)continue;const g=ctx.createLinearGradient(b.x,b.y+b.h,b.x,b.y);g.addColorStop(0,'rgba(88,166,255,0)');g.addColorStop(.35,'rgba(88,166,255,.45)');g.addColorStop(1,'#e6edf3');ctx.fillStyle=g;ctx.fillRect(b.x,b.y,b.w,b.h);}
        ctx.shadowColor='#79c0ff';
        for(let i=0;i<gs.bullets.length;i++){const b=gs.bullets[i];if(!b.aimed||b.laser)continue;ctx.fillStyle='#e6edf3';ctx.fillRect(b.x,b.y,b.w,b.h);ctx.fillStyle='#79c0ff';ctx.fillRect(b.x+1,b.y+3,b.w-2,b.h-6);}
        ctx.shadowBlur=0;
        for(let i=0;i<gs.bullets.length;i++){const b=gs.bullets[i];if(!b.laser||!gs.bossShip)continue;const op=b.life/.2,sx=b.x+b.w/2,sy=gs.p.y+gs.p.h*.15;const dist=Math.hypot(b.tX-sx,b.tY-sy),ang=Math.atan2(b.tY-sy,b.tX-sx);const pw=3+Math.sin(laserP);ctx.save();ctx.translate(sx,sy);ctx.rotate(ang);const og=ctx.createLinearGradient(0,-pw*3,0,pw*3);og.addColorStop(0,'rgba(248,81,73,0)');og.addColorStop(.5,`rgba(248,81,73,${op*.15})`);og.addColorStop(1,'rgba(248,81,73,0)');ctx.fillStyle=og;ctx.shadowBlur=10;ctx.shadowColor=`rgba(248,81,73,${op*.45})`;ctx.fillRect(0,-pw*3,dist,pw*6);const cg=ctx.createLinearGradient(0,-pw,0,pw);cg.addColorStop(0,`rgba(255,80,80,${op*.42})`);cg.addColorStop(.5,`rgba(255,255,255,${op})`);cg.addColorStop(1,`rgba(255,80,80,${op*.42})`);ctx.fillStyle=cg;ctx.shadowBlur=11;ctx.fillRect(0,-pw,dist,pw*2);ctx.shadowBlur=0;ctx.restore();ctx.save();ctx.globalAlpha=op*.55;ctx.shadowBlur=6;ctx.shadowColor='#f85149';ctx.fillStyle='#ffb0aa';ctx.beginPath();ctx.arc(sx,sy,4.5,0,6.2832);ctx.fill();ctx.shadowBlur=0;ctx.restore();}
        let anyMF=false;for(let i=0;i<8;i++)if(ML[i]>0){anyMF=true;break;}
        if(anyMF){ctx.shadowBlur=8;ctx.shadowColor='#58a6ff';for(let i=0;i<8;i++){if(ML[i]<=0)continue;ctx.globalAlpha=ML[i]*.65;ctx.fillStyle='rgba(230,237,243,.8)';ctx.beginPath();ctx.arc(MX[i],MY[i],3.5*ML[i],0,6.2832);ctx.fill();}ctx.globalAlpha=1;ctx.shadowBlur=0;}
        if(gs.enemies.length){
          ctx.shadowBlur=5;ctx.shadowColor='rgba(248,81,73,.5)';
          for(let ei=0;ei<gs.enemies.length;ei++){
            const e=gs.enemies[ei],ecx=e.x+e.w/2,ecy=e.y+e.h/2;
            if(e.sa<1||e.pulled){ctx.save();ctx.translate(ecx,ecy);ctx.scale(e.sa*(e.pullSc||1),e.sa*(e.pullSc||1));ctx.translate(-ecx,-ecy);}
            const eg=ctx.createLinearGradient(e.x,ecy,e.x+e.w,ecy);
            if(e.big){eg.addColorStop(0,'#450a0a');eg.addColorStop(.35,'#991b1b');eg.addColorStop(.5,'#dc2626');eg.addColorStop(.65,'#991b1b');eg.addColorStop(1,'#450a0a');}
            else{eg.addColorStop(0,'#6b1414');eg.addColorStop(.35,'#b91c1c');eg.addColorStop(.5,'#ef4444');eg.addColorStop(.65,'#b91c1c');eg.addColorStop(1,'#6b1414');}
            ctx.fillStyle=eg;
            ctx.beginPath();ctx.moveTo(ecx,e.y+e.h);ctx.lineTo(e.x+e.w*.35,e.y+e.h*.75);ctx.lineTo(e.x+e.w*.35,e.y+e.h*.45);ctx.lineTo(e.x+e.w*.4,e.y+e.h*.3);ctx.lineTo(ecx,e.y+e.h*.35);ctx.lineTo(e.x+e.w*.6,e.y+e.h*.3);ctx.lineTo(e.x+e.w*.65,e.y+e.h*.45);ctx.lineTo(e.x+e.w*.65,e.y+e.h*.75);ctx.closePath();ctx.fill();
            ctx.fillStyle=e.big?'#6b1414':'#7f1d1d';
            ctx.beginPath();ctx.moveTo(e.x+e.w*.35,e.y+e.h*.6);ctx.lineTo(e.x-3,e.y+e.h*.5);ctx.lineTo(e.x,e.y+e.h*.26);ctx.lineTo(e.x+e.w*.25,e.y+e.h*.28);ctx.lineTo(e.x+e.w*.35,e.y+e.h*.4);ctx.closePath();ctx.fill();
            ctx.beginPath();ctx.moveTo(e.x+e.w*.65,e.y+e.h*.6);ctx.lineTo(e.x+e.w+3,e.y+e.h*.5);ctx.lineTo(e.x+e.w,e.y+e.h*.26);ctx.lineTo(e.x+e.w*.75,e.y+e.h*.28);ctx.lineTo(e.x+e.w*.65,e.y+e.h*.4);ctx.closePath();ctx.fill();
            if(e.big&&e.maxHp>1)for(let hp=0;hp<e.maxHp;hp++){ctx.fillStyle=hp<e.hp?'rgba(248,81,73,.8)':'rgba(30,30,30,.8)';ctx.fillRect(ecx-e.maxHp*5+hp*11,e.y-9,8,3);}
            if(e.sa<1||e.pulled)ctx.restore();
          }
          ctx.shadowBlur=0;
        }
        if(gs.bossMode&&gs.bossShip){
          const bs=gs.bossShip;
          if(gs.bossDying)ctx.globalAlpha=1-gs.bossDp;
          if(bossImgReady&&bossImg){
            const iw=bossImg.naturalWidth||800,ih=bossImg.naturalHeight||450;
            const scale=Math.min(bs.w/ih, bs.h/iw)*1.9;
            const dw=iw*scale, dh=ih*scale;
            const bcx2=bs.x+bs.w/2, bcy2=bs.y+bs.h/2;
            ctx.save();
            ctx.translate(bcx2, bcy2);
            ctx.rotate(-Math.PI/2); // 90° anti-clockwise
            if(bs.hitFlash>0.25){ctx.filter=`brightness(${1+bs.hitFlash*1.6}) saturate(2.2) hue-rotate(-10deg)`;}
            ctx.drawImage(bossImg,-dw/2,-dh/2,dw,dh);
            ctx.filter='none';
            ctx.restore();
          }else{
            ctx.drawImage(getBoss(gs.bossEnraged,bs.hitFlash),bs.x-40,bs.y);
          }
          if(gs.bossDying)ctx.globalAlpha=1;
          if(!gs.bossDying){
            const bx=bs.x,by=bs.y,bcx=bx+bs.w/2;
            const barW=bs.w*1.08,barH=15,barX=bx-(barW-bs.w)/2,barY=by-48;
            const hpPct=Math.max(0,bs.hp/bs.maxHp);
            const hc=hpPct>.6?'#238636':hpPct>.3?'#9e6a03':'#b62324';
            ctx.fillStyle='rgba(8,12,18,.93)';ctx.fillRect(barX,barY,barW,barH);
            ctx.strokeStyle='rgba(40,48,58,.7)';ctx.lineWidth=1;ctx.strokeRect(barX,barY,barW,barH);
            ctx.fillStyle=hc;ctx.fillRect(barX+1,barY+1,(barW-2)*hpPct,barH-2);
            ctx.fillStyle='rgba(0,0,0,.2)';for(let t=1;t<10;t++)ctx.fillRect(barX+barW*t*.1-.5,barY,1,barH);
            ctx.fillStyle='rgba(215,225,238,.7)';ctx.font="bold 9px 'Orbitron'";ctx.textAlign='center';
            ctx.fillText(gs.bossEnraged?'⚔ ARIES BATTLECRUISER — ENRAGED ⚔':'⚔ ARIES BATTLECRUISER ⚔',bcx,barY-12);
            ctx.fillStyle='rgba(120,132,148,.55)';ctx.font="8px 'JetBrains Mono'";
            ctx.fillText(`${Math.ceil(bs.hp)} / ${bs.maxHp}  (${Math.round(hpPct*100)}%)`,bcx,barY-2);
            ctx.textAlign='left';
          }
        }
        {
          const pl=gs.p,plcx=pl.x+pl.w/2;
          const egR=(gs.bossMode?22:14)*(.75+.25*Math.sin(pl.t*.5));
          const eg=ctx.createRadialGradient(plcx,pl.y+pl.h*.72,0,plcx,pl.y+pl.h*.72,egR);
          eg.addColorStop(0,`rgba(88,166,255,${gs.bossMode?.8:.65})`);eg.addColorStop(.4,`rgba(56,130,240,${gs.bossMode?.42:.26})`);eg.addColorStop(1,'rgba(30,64,175,0)');
          ctx.fillStyle=eg;ctx.beginPath();ctx.arc(plcx,pl.y+pl.h*.72,egR,0,6.2832);ctx.fill();
          [pl.x+pl.w*.14,pl.x+pl.w*.86].forEach(wx=>{const wg=ctx.createRadialGradient(wx,pl.y+pl.h*.7,0,wx,pl.y+pl.h*.7,7);wg.addColorStop(0,'rgba(56,189,248,.45)');wg.addColorStop(1,'rgba(30,64,175,0)');ctx.fillStyle=wg;ctx.beginPath();ctx.arc(wx,pl.y+pl.h*.7,7,0,6.2832);ctx.fill();});
          ctx.drawImage(gs.bossMode?spBoss:spNorm,pl.x-SPAD,pl.y-SPAD);
        }
        let anyFN=false;for(let i=0;i<FN;i++)if(fnL[i]>0){anyFN=true;break;}
        if(anyFN){ctx.shadowBlur=4;ctx.font="bold 11px 'Orbitron'";ctx.textAlign='center';for(let i=0;i<FN;i++){if(fnL[i]<=0)continue;const op=fnL[i]>.7?1:fnL[i]/.7;ctx.globalAlpha=op;ctx.shadowColor=fnC[i];ctx.fillStyle=fnC[i];ctx.fillText(fnT[i],fnX[i],fnY[i]);}ctx.globalAlpha=1;ctx.shadowBlur=0;ctx.textAlign='left';}
        if(scFlash>0){ctx.fillStyle=flashRed?`rgba(248,81,73,${Math.min(.42,scFlash*.3)})`:`rgba(227,179,65,${Math.min(.42,scFlash*.3)})`;ctx.fillRect(0,0,W,H);}
      }

      let lastT=performance.now();
      function loop(t){
        const raw=Math.min(t-lastT,MAXDT);lastT=t;acc+=raw;gt+=raw;
        let steps=0;
        while(acc>=FIXED&&steps<3){update(FIXED,gt);acc-=FIXED;steps++;}
        draw(raw);
        rafId=requestAnimationFrame(loop);
      }
      rafId=requestAnimationFrame(loop);

      gameKeyDn=e=>{keysRef.current[e.code]=true;if(e.code==='Escape'&&gs.active)die(true);};
      gameKeyUp=e=>{keysRef.current[e.code]=false;};
      window.addEventListener('keydown',gameKeyDn);
      window.addEventListener('keyup',gameKeyUp);
    }

    runTerminal();

    return()=>{
      stopGame();
      window.removeEventListener('keydown',globalKey);
    };
  },[]);

  return(
    <div className="wd">
      <div ref={termRef} className="tw">
        <div className="tbox">
          <div className="tbar">
            <div className="tdot tdr"/><div className="tdot tdy"/><div className="tdot tdg"/>
            <span className="ttl">pilot@warp-drive — zsh — 120×36</span>
          </div>
          <div className="tscroll"><div className="tout"/></div>
          <div className="tinrow" style={{display:'none'}}>
            <span className="tprompt"><span className="hi">pilot@warp-drive</span>:<span className="p2">~</span>&nbsp;$&nbsp;</span>
            <input className="tinp" autoComplete="off" spellCheck="false"/>
          </div>
        </div>
      </div>

      <div ref={introRef} className="wi">
        <div className="wic tl"/><div className="wic tr"/>
        <div className="wic bl"/><div className="wic br"/>
        <div className="wit">WARP DRIVE</div>
        <div className="wil"/>
        <div ref={introSubRef} className="wis">INITIALIZING WARP CORE...</div>
      </div>

      <canvas ref={canvasRef} className="gc"/>

      <div ref={hudRef} className="hud">
        <div className="htop">
          <div className="hsc">
            <div className="hslbl">Score</div>
            <div ref={scoreRef} className="hsval">SCORE: 0</div>
          </div>
          <div className="hmid">
            <div className="hmrow">
              <span className="hmlbl">Mission Progress</span>
              <span ref={mvalRef} className="hmval">0 / 800</span>
            </div>
            <div className="htrack">
              <div ref={fillRef} className="hfill" style={{width:'0%',background:'linear-gradient(90deg,#1f6feb,#58a6ff)'}}/>
            </div>
          </div>
          <div className="hrt">
            <span ref={hintRef} className="hhint">WASD / ARROWS · AUTO-FIRE · ESC EXIT</span>
            <span ref={bossAlertRef} className="hboss">⚠ BOSS BATTLE ⚠</span>
          </div>
        </div>
        <div ref={comboRef} className="hcombo"/>
      </div>

      <div ref={bwarnRef} className="bwarn">
        <div className="bwbg"/><div className="bwscan"/>
        <div className="bwc">
          <div className="bweye">THREAT LEVEL: EXTREME</div>
          <div className="bwmain">WARNING</div>
          <div className="bwsub">ARIES BATTLECRUISER INCOMING</div>
          <div className="bwbars">{Array.from({length:13},(_,i)=><div key={i} className="bwbar"/>)}</div>
        </div>
      </div>

      <div ref={laserRef} className="lnotif">🔴 LASER ACTIVATED</div>

      <div ref={goRef} className="go">
        <div className="goeye">SECTOR DEFENSE</div>
        <h2 className="goh">MISSION COMPLETE</h2>
        <div className="goline"/>
        <p className="gosc">FINAL SCORE: 0 / 800</p>
        <button className="gobtn">RESTART MISSION</button>
      </div>

      <div className="cb cbtl"/><div className="cb cbtr"/>
      <div className="cb cbbl"/><div className="cb cbbr"/>

      <div className="mob">
        <button className="mobbtn"
          onTouchStart={e=>{e.preventDefault();keysRef.current['ArrowLeft']=true;}}
          onTouchEnd={e=>{e.preventDefault();keysRef.current['ArrowLeft']=false;}}
          onTouchCancel={e=>{e.preventDefault();keysRef.current['ArrowLeft']=false;}}>←</button>
        <button className="mobbtn"
          onTouchStart={e=>{e.preventDefault();keysRef.current['ArrowRight']=true;}}
          onTouchEnd={e=>{e.preventDefault();keysRef.current['ArrowRight']=false;}}
          onTouchCancel={e=>{e.preventDefault();keysRef.current['ArrowRight']=false;}}>→</button>
      </div>
    </div>
  );
}
